// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { CustomerConfig } from "utils/ConfigTypes";
import React from "react";
import SymptomsCheckerPanel from "components/SymptomsChecker/SymptomsCheckerPanel";
import SymptomsDetailPanel from "components/SymptomsChecker/SymptomsDetailPanel";
import WhatDoYouSee2Panel from "components/WhatDoYouSee/WhatDoYouSee2Panel";
import WhatDoYouSeePanel from "components/WhatDoYouSee/WhatDoYouSeePanel";
import { fbt } from "fbt";

export const QuidelConfig: CustomerConfig = {
  id: "cue",
  name: "Cue",
  pathPrefix: "cue",
  allowGuestRuns: true,
  landingPage: "NEWTESTRUN",
  customThemeOverride: {},
  headerConfig: {
    headerLogo: { label: "Cue", position: "center" },
  },
  loginImageUrl: "AudereWhiteLogo.png",
  loginHeader:
    "Cue enables you to order and conduct a COVID-19 test from the comfort of your home.",
  loginDescription:
    "The COVID-19 Antibody Home Testing App is offered by Audere, and enables you to order and conduct a COVID-19 antibody test from the comfort of your home.",
  showProgressBar: true,

  getTestRunDefinition: () => ({
    gettingStarted: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "Kit box and tissues on a table.",
              "starting step: image alt attribute",
              { project: "audere" }
            ),
            src: "gettingstarted.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Let's get started", "starting step: page title", {
              project: "audere",
            }),
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "This app will guide you through completing a finger-prick COVID-19 antibody test. The entire process will take about 15 minutes.",
              "starting step: intro text",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "Before you start:",
              "starting step: title leading to action list",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "Find a well-lit location with a flat surface where you can do the test.",
                "starting step: well lit",
                { project: "audere" }
              ),
              fbt(
                "Collect two clean tissues and one plaster. These items are not included in the kit.",
                "starting step: collect tissues",
                { project: "audere" }
              ),
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "setupKit",
        },
      },
    },

    setupKit: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "Kit box, lancet, pipette, buffer solution, and test.",
              "Open test kit step: image alt attribute",
              { project: "audere" }
            ),
            src: "opentestkit.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Open your test kit", "Open test kit step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "Paragraph",
          props: {
            children: [
              fbt(
                "Open the test kit and remove (but do not unwrap) the items. Handle all items gently, do not bend or squeeze.",
                "Open test kit step: handle kit",
                {
                  project: "audere",
                }
              ),
              // TODO: Find a way to pass rich text in here.
              <fbt desc="Open test kit step: confirm items" project="audere">
                Confirm that the kit includes the items below, or{" "}
                <a href="/">contact us if an item is missing</a>.
              </fbt>,
            ],
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "white package (test cassette)",
                "Open test kit step: cassette",
                { project: "audere" }
              ),
              fbt("pipette (thin tube)", "Open test kit step: tube", {
                project: "audere",
              }),
              fbt("lancet (finger prick tool)", "Open test kit step: lancet", {
                project: "audere",
              }),
              fbt("small bottle of buffer", "Open test kit step: buffer", {
                project: "audere",
              }),
              fbt("bag for kit disposal", "Open test kit step: disposal bag", {
                project: "audere",
              }),
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "washHands",
        },
        previous: "gettingStarted",
      },
    },

    washHands: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "Soap being put on hands to be washed.",
              "wash hands step: alt description for image",
              { project: "audere" }
            ),
            src: "washhands.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Wash and dry hands", "wash hands step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "Wash your hands for 20 seconds using warm water and soap.",
                "wash hands step: wash 20 seconds",
                {
                  project: "audere",
                }
              ),
              fbt("Dry hands thoroughly.", "wash hands step: dry hands", {
                project: "audere",
              }),
              fbt(
                "Do not apply hand cream or sanitiser.",
                "wash hands step: no cream or sanitiser",
                {
                  project: "audere",
                }
              ),
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "prepareTest",
        },
        previous: "setupKit",
      },
    },

    prepareTest: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "Test cassette, pipette, and lancet.",
              "prepare step: alt description for image",
              { project: "audere" }
            ),
            src: "preparetest.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Prepare for the test", "prepare step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "Open white package and carefully remove the test cassette.",
                "prepare step: take cassette",
                {
                  project: "audere",
                }
              ),
              fbt(
                "Collect pipette (for collecting blood) and lancet (finger prick tool).",
                "prepare step: collect pipette and lancet",
                {
                  project: "audere",
                }
              ),
              fbt(
                "Gently twist and pull the cap off the lancet.",
                "prepare step: pull cap off lancet",
                {
                  project: "audere",
                }
              ),
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "prickfinger",
        },
        previous: "washHands",
      },
    },

    prickfinger: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 176,
            width: 391,
            type: "server",
            alt: fbt(
              "Finger being pricked by lancet.",
              "prick finger step: alt description for image",
              { project: "audere" }
            ),
            src: "fingerPrick.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Prick finger", "prick finger step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "Choose a finger on your non-dominant hand to prick - your third or fourth are typically less sensitive. If you can, stand up when pricking your finger.",
              "prick finger step: choose finger",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt desc="prick finger step: massage hand" project="audere">
                Point your hand down and massage your finger from knuckle to tip
                (use a squeeze &amp; release motion).
              </fbt>,
              <fbt desc="prick finger step: hand flat" project="audere">
                Lay your hand flat on a surface, palm up.
              </fbt>,
              <fbt desc="prick finger step: lancet on finger" project="audere">
                Press the lancet firmly onto your finger tip, not too close to
                the nail.
              </fbt>,
              <fbt
                desc="prick finger step: press lancet's button"
                project="audere"
              >
                Press the button on the top of the lancet until it clicks.
              </fbt>,
              <fbt desc="prick finger step: wipe away blood" project="audere">
                Wipe away the first drop of blood with a tissue (it contains
                fluid that will spoil the test).
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "collectSample",
        },
        previous: "prepareTest",
      },
    },

    collectSample: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 136,
            width: 341,
            type: "server",
            alt: fbt(
              "Finger having its blood drawn out by pipette.",
              "Collect sample step: alt description for image",
              { project: "audere" }
            ),
            src: "bloodPrick.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Collect sample", "Collect sample step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt desc="Collect sample step: squeeze finger" project="audere">
                Gently squeeze your finger so a new blood drop forms.
              </fbt>,
              <fbt
                desc="Collect sample step: pipette touch blood"
                project="audere"
              >
                Hold the pipette horizontally and gently touch the tip of the
                pipette to the drop of blood - do not squeeze the pipette.
              </fbt>,
              <fbt desc="Collect sample step: collect blood" project="audere">
                The pipette will automatically collect the blood.
              </fbt>,
              <fbt
                desc="Collect sample step: continue squeeze"
                project="audere"
              >
                Continue squeezing your finger and collecting blood until the
                pipette is filled to the black line.
              </fbt>,
              <fbt
                desc="Collect sample step: lay pipette down"
                project="audere"
              >
                When filled, carefully lay pipette down so the tip with blood
                isn’t touching anything.
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "wipeAndPlasterFinger",
        },
        previous: "prickfinger",
      },
    },

    wipeAndPlasterFinger: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "A plaster being applied to a finger.",
              "Wipe and plaster step: alt description for image",
              { project: "audere" }
            ),
            src: "plasterfinger.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt(
              "Wipe and plaster finger",
              "Wipe and plaster step: title",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt desc="Wipe and plaster step: wipe finger" project="audere">
                Gently wipe finger with clean tissue.
              </fbt>,
              <fbt desc="Wipe and plaster step: apply plaster" project="audere">
                Apply plaster or similar item to your finger.
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "addSample",
        },
        previous: "collectSample",
      },
    },
    addSample: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 278,
            width: 180,
            type: "server",
            alt: fbt(
              "Blood sample being dropped into test cassette.",
              "Add sample to cassette step: alt description for image",
              { project: "audere" }
            ),
            src: "bloodDrop.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt(
              "Add blood sample to test cassette",
              "Add sample to cassette step: title",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt
                desc="Add sample to cassette step: place cassette"
                project="audere"
              >
                Place the test cassette in front of you.
              </fbt>,
              <fbt
                desc="Add sample to cassette step: position pipette"
                project="audere"
              >
                Position the pipette just above the bottom hole on the test
                cassette.
              </fbt>,
              <fbt
                desc="Add sample to cassette step: empty pipette"
                project="audere"
              >
                When ready, gently, and slowly squeeze the pipette into the hole
                until it is empty.
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "addLiquid",
        },
        previous: "wipeAndPlasterFinger",
      },
    },
    addLiquid: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 187,
            width: 159,
            type: "server",
            alt: fbt(
              "Solution being dropped into test cassette.",
              "Add liquid step: alt description for image",
              { project: "audere" }
            ),
            src: "solutionDrop.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt(
              "Add liquid to test cassette",
              "Add liquid step: title",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt desc="Add liquid step: open buffer" project="audere">
                Open the small bottle of buffer.
              </fbt>,
              <fbt desc="Add liquid step: hold bottle" project="audere">
                Hold the bottle upside down just above the bottom hole on the
                test cassette.
              </fbt>,
              <fbt desc="Add liquid step: squeeze bottle" project="audere">
                Slowly and carefully squeeze the bottle so only TWO drops fall
                into the hole.
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      nav: {
        next: {
          default: "listSymptoms",
        },
        previous: "addSample",
      },
    },
    listSymptoms: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: fbt("Symptom survey", "Symptom survey: title", {
              project: "audere",
            }),
          },
        },
        {
          type: React.memo(SymptomsCheckerPanel),
          props: {},
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "symptomsDetail",
          noSymptoms: "waitStripReaction",
        },
        previous: "addLiquid",
      },
    },
    symptomsDetail: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: fbt("Symptom severity", "Symptom severity: title", {
              project: "audere",
            }),
          },
        },
        {
          type: React.memo(SymptomsDetailPanel),
          props: {},
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "waitStripReaction",
        },
        previous: "listSymptoms",
      },
    },
    waitStripReaction: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: fbt(
              "Please wait 10 minutes",
              "Wait reaction step: title",
              { project: "audere" }
            ),
          },
        },
        {
          type: "WaitStep",
          props: {},
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "The test cassette will take ten minutes to process.",
                "Wait reaction step: ten minutes",
                { project: "audere" }
              ),
              fbt(
                "Once the timer is complete, you must take a photo of the test cassette within five minutes to ensure accurate results.",
                "Wait reaction step: once complete take photo",
                { project: "audere" }
              ),
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "whatDoYouSee",
        },
        previous: "symptomsDetail",
      },
    },
    whatDoYouSee: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: fbt("What do you see?", "What do you see: title", {
              project: "audere",
            }),
          },
        },
        {
          type: React.memo(WhatDoYouSeePanel),
          props: {},
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "whatDoYouSee2",
          noLine: "scanStrip",
        },
        previous: "waitStripReaction",
      },
    },
    whatDoYouSee2: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: fbt("What do you see?", "What do you see: title", {
              project: "audere",
            }),
          },
        },
        {
          type: React.memo(WhatDoYouSee2Panel),
          props: {},
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "scanStrip",
        },
        previous: "whatDoYouSee",
      },
    },

    scanStrip: {
      contentDescription: [
        {
          type: "PhotoUploaderPanel",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        previous: "whatDoYouSee2",
      },
    },
  }),
};

export default QuidelConfig;
